import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import iconPage from 'stile-shared/assets/icons/icon_page.svg';

import { Button } from 'stile-shared/src/components/2024/common/Button';
import { HearFromSection } from 'stile-shared/src/components/2024/common/HearFromSection';
import { Icon } from 'stile-shared/src/components/2024/common/Icon';
import { RealWorldSection } from 'stile-shared/src/components/2024/common/RealWorldSection';
import PageLinkFeature from 'stile-shared/src/components/2024/layout/PageLinkFeature';
import { TopSection } from 'stile-shared/src/components/2024/layout/RegionBasedTopSection';
import ReviewsSlider from 'stile-shared/src/components/2024/layout/ReviewsSlider/ReviewsSlider';
import { SideBySideSection } from 'stile-shared/src/components/2024/layout/SideBySideSection';

import { PageLayout } from 'templates/2024/PageLayout';

export { Head } from 'templates/2024/Head';

const WALayout = styled(PageLayout)`
  p {
    margin-bottom: 12px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  @media (max-width: 768px) {
    .header-wrapper {
      background-color: var(--stile-grey-1);
    }
  }
`;

const WATopSection = styled(TopSection)`
  width: 100%;
  p {
    margin-bottom: 12px;
    &:last-child {
      margin-bottom: 0;
    }
  }
  .content {
    padding: 80px 0;
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-gap: 20px;

    .--left {
      width: 100%;
      grid-column: 1 / 7; /* 6 columns */
      display: flex;
      flex-direction: column;
      gap: 0;

      h1 {
        margin: 40px 0px 32px;
        max-width: 530px;
        width: 100%; /* full width of the 6 columns */
      }

      .--feature_heading {
        margin: 0;
        width: 83.33%; /* 5/6 of the left container = 5/12 of total */
      }
    }

    .--right {
      width: 100%;
      grid-column: 7 / 13; /* 6 columns */
      display: flex;
      align-items: center;
      justify-content: center;

      .gatsby-image-wrapper {
        margin-top: 0px;
        aspect-ratio: 1126 / 1002;
        width: 100%;
        height: auto;
      }
    }
  }
  @media (max-width: 768px) {
    background-color: var(--stile-grey-1);
    .content {
      padding: 0 25px 60px 25px;
      margin: 0;
      display: block;

      .--left {
        h1 {
          margin: 25px 0px 20px;
        }

        .--feature_heading {
          margin: 0 0 30px;
          width: 100%;
        }
      }

      .--right {
        width: 100%;
        .gatsby-image-wrapper {
          width: 100% !important;
          height: auto;
          margin-bottom: 0px;
          aspect-ratio: 1126 / 1002;

          img {
            object-fit: contain !important;
            width: 100% !important;
            height: 100% !important;
          }
        }
      }
    }
  }
`;

const StyledImageWrapper = styled.div`
  display: flex;
  min-width: 267px;
  justify-content: center;
  .greyBorder {
    border-color: var(--stile-grey-1);
    border-style: solid;
    border-width: 2px;
  }
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.2);
  }

  .gatsby-image-wrapper,
  .overlay {
    border-radius: 32px;
  }
  .cover,
  .withShadow,
  .overlay {
    box-shadow: 0px 41.958px 25.37px 0px rgba(0, 0, 0, 0.04),
      0px 18.54px 18.54px 0px rgba(0, 0, 0, 0.06), 0px 4.879px 10.733px 0px rgba(0, 0, 0, 0.07);
  }
  @media (max-width: 768px) {
    .gatsby-image-wrapper,
    .overlay {
      border-radius: 16px !important;
    }
  }
`;

const CurriculumSection = styled(SideBySideSection)`
  margin-top: 150px;
  margin-bottom: 150px;
  .right-text a {
    margin-top: 32px;
  }
  h2 {
    margin-bottom: 32px;
    margin-top: 0;
  }

  @media (max-width: 768px) {
    margin-top: 60px;
    margin-bottom: 60px;
    .right-text a {
      margin-top: 28px;
    }
  }
`;

function WASyllabusPage(props: PageProps) {
  return (
    <WALayout {...props}>
      <WATopSection>
        <div className="content">
          <div className="--left">
            <h1>The complete science program for the revised WA Curriculum</h1>
            <p className="--feature_heading">
              Aligned to the new Western Australian Curriculum, Stile is the only complete program
              with a ready-to-teach scope and sequence.
            </p>
          </div>
          <div className="--right">
            <StaticImage
              src="../../../../stile-shared/assets/images/wa/wa_map.png"
              alt="Map of Western Australia with many green glowing dots showing the locations of Stile schools, with small illustrations of students and a scientist around the edges."
              loading="eager"
            />
          </div>
        </div>
      </WATopSection>

      <ReviewsSlider />

      <CurriculumSection>
        <div className="image-left content">
          <div className="left-img">
            <StyledImageWrapper>
              <StaticImage
                className="cover"
                src="../../../../stile-shared/assets/images/wa/wa_imp_guide.jpg"
                alt="Cover page of a document titled 'Western Australian Curriculum: Our guide to preparing for implementation.' Stile logo in green on a light background."
                placeholder="none"
                loading="lazy"
              />
            </StyledImageWrapper>
          </div>
          <div className="right-text">
            <h2>Built for the WA curriculum. Ready for every classroom.</h2>
            <p>
              The <b>revised Western Australian curriculum</b> brings more structure, new content
              and clearer expectations. Stile is on track to be completely aligned by the end of
              2025, so you’ll be ready for 2026. Our updated units cover all content for Years 7–10,
              with lessons aligned to the SCSA learning and assessment outcomes.
            </p>
            <p>
              The result is a seamless, structured program that saves teachers time while supporting
              consistency across classrooms.
            </p>
            <p>
              <b>Save hours of planning</b>
              <br />
              Get our Implementation Guide to learn about how the curriculum is changing and how you
              can be prepared with our fully-aligned scope and sequence for years 7-10 science.
            </p>
            <Button
              className="iconButton"
              newTab={true}
              href="/resources/wa-curriculum/"
              label={
                <>
                  <Icon src={iconPage} iconSize="20px" style={{ marginRight: 6 }} />
                  <span>Download implementation guide</span>
                </>
              }
            />
          </div>
        </div>
      </CurriculumSection>

      <HearFromSection
        heading="How teachers are bringing the Australian Curriculum to life in their classroom"
        subheading={
          <>
            <p>
              Stile’s scope and sequence is a world-class starting point that’s fully editable and
              customisable to your school’s outcomes, priorities and teaching styles.
            </p>
            <p>
              From ready-to-adapt program design to student progress feedback, Stile supports the
              complete teaching workflow with customisable lesson plans, always up-to-date content,
              high-quality instructional materials and strategies to balance device use and increase
              student learning and engagement.
            </p>
          </>
        }
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/wa/wa_testimonial_video_cover.jpg"
            alt="thumbnail from Stile YouTube testimonial video"
          />
        }
        youtubeUrl="https://www.youtube.com/watch?v=4lFzzbHRNpY"
      />

      <RealWorldSection
        heading="What’s new in the WA Curriculum for 2026"
        tabs={[
          'Curriculum structure',
          'WA contexts in science',
          'Science inquiry practices',
          'First Nations knowledges',
        ]}
        headings={[
          'Bringing science together',
          'Making science relevant to WA',
          'From skills to practices',
          'Incorporating First Nations histories and cultures',
        ]}
        multiParagraphs={[
          [
            <>
              The three-strand model of Science Understanding, Science as a Human Endeavour and
              Science Inquiry Skills has been streamlined into two strands:{' '}
              <b>Science Understanding</b> and <b>Science Inquiry</b>. A new sub-strand,{' '}
              <b>Collaborating and Applying</b>, now carries the application and relevance of
              science into real-world contexts.
            </>,
            'In Stile, every aspect of the Western Australian Science curriculum is supported by units that bring science to life in real-world contexts.',
            'This integrated approach gives teachers a clear scope and sequence across Years 7–10, aligning directly with the Western Australian Curriculum. It ensures science is connected and meaningful for students while providing departments with a structured progression through all content.',
          ],
          [
            'The revised curriculum includes links to Western Australia’s industries and environment. Earth and space sciences incorporate space exploration and mining, while biological sciences provide an opportunity to learn about Western Australia’s unique biodiversity.',
            'In Stile, this context is embedded in units such as Active Earth, which examines the properties of rocks and minerals, and the extraction of resources, providing a link to the mining industry. In Ecosystems, students learn about ecological monitoring and human impacts on ecosystem health, providing a connection to the Western Australian environment. These units provide students with authentic connections between classroom learning and the world they live in.',
          ],
          [
            'What was once Science Inquiry Skills is now Science Inquiry, reflecting a shift towards broader inquiry practices. These include Questioning and predicting, Planning and conducting, Processing, modelling and analysing, Evaluating, Communicating and Collaborating and applying.',
            'In Stile, students develop these practices across Years 7–10 as they engage in a range of investigations at every year level. In Plants - Lunar Edition, students explore factors that affect plant growth to identify which plants might survive space travel and grow successfully on the Moon. This change means inquiry is no longer taught as a separate checklist of skills, but woven into every focus area.',
          ],
          [
            'The Western Australian Curriculum embeds Aboriginal and Torres Strait Islander Histories and Cultures as a cross-curriculum priority.',
            'In Stile, this is woven through units in ways that are respectful and practical for classrooms. Students investigate Aboriginal spear throwers as speed multipliers in Forces, explore Aboriginal astronomy to predict phenomena in Our Place in Space, and learn about the use of traditional medicinal plants in The Immune System. All resources are reviewed for cultural safety, giving teachers confidence that First Nations knowledge is acknowledged and shared respectfully as part of everyday science learning.',
          ],
        ]}
        images={[
          <StyledImageWrapper key={0}>
            <StaticImage
              className="greyBorder"
              src="../../../../stile-shared/assets/images/wa/wa_whats_new_tab_1.jpg"
              alt="Curriculum content areas displayed with images and text: Term 1 'Biological sciences' with topics on ecosystems and evolution, and Term 2 'Chemical sciences' with topics on matter and reactions."
            />
          </StyledImageWrapper>,
          <StyledImageWrapper key={1}>
            <StaticImage
              src="../../../../stile-shared/assets/images/wa/wa_whats_new_tab_2.jpg"
              alt="Three students in school uniforms wearing safety goggles and blue aprons working on a science experiment at a lab bench."
            />
          </StyledImageWrapper>,
          <StyledImageWrapper key={2}>
            <StaticImage
              src="../../../../stile-shared/assets/images/wa/wa_whats_new_tab_3.jpg"
              alt="Two high school students looking closely at plants in a plastic tub, with more greenery in the foreground."
            />
          </StyledImageWrapper>,
          <StyledImageWrapper key={3}>
            <StaticImage
              src="../../../../stile-shared/assets/images/wa/wa_whats_new_tab_4.jpg"
              alt="Close-up of several Aboriginal tools with black resin-coated tips and wooden handles, some painted or decorated, laid out in a row."
            />
          </StyledImageWrapper>,
        ]}
      />

      <PageLinkFeature
        heading="See how Stile can support your science teaching with our curriculum expert in Western Australia"
        copy={
          <div>
            <p>
              Stile is all about empowering teachers with the best support. In Western Australia,
              that means teaming up with <b>Steve Dornan</b>, who has extensive experience in
              teaching, leadership, administration and instructional coaching.
            </p>
          </div>
        }
        image={
          <StaticImage
            src="../../../../stile-shared/assets/images/wa/portrait.jpg"
            alt="Stile team member Steve Dornan against a green science-themed background."
            quality={100}
          />
        }
        button={
          <Button
            className="setUpTrialButton"
            href="/set-up-trial/"
            label={
              <>
                <span>Learn about trialling Stile at your school</span>
              </>
            }
          />
        }
      />
    </WALayout>
  );
}

export default WASyllabusPage;
